import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileText, RefreshCw, CheckCircle, Clock, TrendingUp, AlertTriangle, Activity, PieChart, Wallet, Target, CircleDollarSign, CalendarClock, ListChecks, CalendarDays, BarChart3, Mail, Landmark, Repeat, CreditCard, Banknote, Euro, Trash2 } from 'lucide-react';

import { getPaymentMethodConfig } from './constants';

const _RECHARTS_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function DashboardTab({
  dashboardStats, stats: stats, calculatedComptaStats: calculatedComptaStats, refreshing,
  calculateMontantTTC, openValidationModal, setActiveTab, fetchData,
  documentsEnAttente: documentsEnAttente,
  openPartialPaymentModal, printEcheancier, handleResetEcheancier, handleDeleteEcheancier
}) {
  return (
        <div className="space-y-6" data-testid="dashboard-content">
          {/* En-tête du Dashboard */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Vue d'ensemble financière</h2>
              <p className="text-gray-500 text-sm mt-1">Suivi en temps réel des contributions ESS</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-gray-500">Dernière mise à jour</p>
                <p className="text-sm font-semibold text-gray-700">{new Date().toLocaleString('fr-FR')}</p>
              </div>
              <Button onClick={() => fetchData(true)} disabled={refreshing} variant="outline" className="gap-2">
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>

          {/* KPIs principaux - Design Premium */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Total Dû */}
            <Card className="relative overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-zinc-900 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvZz48L3N2Zz4=')] opacity-30" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-white/60 animate-pulse" />
                      <p className="text-slate-300 text-sm font-medium tracking-wide uppercase">Montant Total Dû</p>
                    </div>
                    <p className="text-4xl font-black tracking-tight">
                      {dashboardStats.totalDu.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
                      <span className="text-xl ml-1 font-normal text-slate-400">€</span>
                    </p>
                    <div className="flex items-center gap-2 text-slate-400">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">{dashboardStats.totalDocuments} document(s)</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm group-hover:bg-white/20 transition-colors">
                    <Target className="h-8 w-8" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Encaissé */}
            <Card className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-300/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-emerald-200 animate-pulse" />
                      <p className="text-emerald-100 text-sm font-medium tracking-wide uppercase">Total Encaissé</p>
                    </div>
                    <p className="text-4xl font-black tracking-tight">
                      {dashboardStats.totalPaye.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
                      <span className="text-xl ml-1 font-normal text-emerald-200">€</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/20 rounded-full">
                        <TrendingUp className="h-3.5 w-3.5" />
                        <span className="text-sm font-semibold">{dashboardStats.tauxRecouvrement}%</span>
                      </div>
                      <span className="text-emerald-200 text-sm">recouvré</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm group-hover:bg-white/30 transition-colors">
                    <Wallet className="h-8 w-8" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reste à Encaisser */}
            <Card className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-amber-200 animate-pulse" />
                      <p className="text-amber-100 text-sm font-medium tracking-wide uppercase">Reste à Encaisser</p>
                    </div>
                    <p className="text-4xl font-black tracking-tight">
                      {dashboardStats.resteAPayer.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
                      <span className="text-xl ml-1 font-normal text-amber-200">€</span>
                    </p>
                    <div className="flex items-center gap-2 text-amber-200">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">{dashboardStats.documentsEnAttente} en attente</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm group-hover:bg-white/30 transition-colors">
                    <CircleDollarSign className="h-8 w-8" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Paiements Partiels */}
            <Card className="relative overflow-hidden bg-gradient-to-br from-purple-500 via-violet-500 to-indigo-600 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-purple-200 animate-pulse" />
                      <p className="text-purple-100 text-sm font-medium tracking-wide uppercase">Paiements Partiels</p>
                    </div>
                    <p className="text-4xl font-black tracking-tight">
                      {dashboardStats.paiementsPartielsEnCours}
                      <span className="text-xl ml-1 font-normal text-purple-200">dossiers</span>
                    </p>
                    <div className="flex items-center gap-2 text-purple-200">
                      <PieChart className="h-4 w-4" />
                      <span className="text-sm">{dashboardStats.montantPartielRecu.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € reçu</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm group-hover:bg-white/30 transition-colors">
                    <ListChecks className="h-8 w-8" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Jauge de progression - Design Premium */}
          <Card className="border-0 shadow-xl bg-white overflow-hidden">
            <CardHeader className="pb-4 border-b bg-gradient-to-r from-gray-50 to-slate-50">
              <CardTitle className="text-lg flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-indigo-600" />
                </div>
                Progression du Recouvrement
                <Badge className="ml-auto bg-indigo-600 text-white font-bold px-3">
                  {dashboardStats.tauxRecouvrement}%
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Barre de progression stylisée */}
                <div className="relative">
                  <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden shadow-inner">
                    <div 
                      className="bg-gradient-to-r from-emerald-400 via-green-500 to-teal-500 h-6 rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-3 relative overflow-hidden"
                      style={{ width: `${Math.min(Math.max(dashboardStats.tauxRecouvrement, 5), 100)}%` }}
                    >
                      <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] animate-shimmer" />
                      {dashboardStats.tauxRecouvrement >= 15 && (
                        <span className="text-white text-xs font-bold relative z-10">{dashboardStats.tauxRecouvrement}%</span>
                      )}
                    </div>
                  </div>
                  {/* Marqueurs de progression */}
                  <div className="flex justify-between mt-2 text-xs text-gray-400 px-1">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>

                {/* Détails du recouvrement */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="relative overflow-hidden p-5 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border border-emerald-100 group hover:shadow-lg transition-all">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-emerald-500 rounded-lg">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-emerald-700">Encaissé</span>
                    </div>
                    <p className="text-2xl font-black text-emerald-600">
                      {dashboardStats.totalPaye.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                    </p>
                  </div>
                  
                  <div className="relative overflow-hidden p-5 bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl border border-purple-100 group hover:shadow-lg transition-all">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-purple-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-purple-500 rounded-lg">
                        <Activity className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-purple-700">Partiels en cours</span>
                    </div>
                    <p className="text-2xl font-black text-purple-600">
                      {dashboardStats.montantPartielRestant.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                    </p>
                  </div>
                  
                  <div className="relative overflow-hidden p-5 bg-gradient-to-br from-red-50 to-red-50 rounded-2xl border border-red-100 group hover:shadow-lg transition-all">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-red-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-red-500 rounded-lg">
                        <Clock className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-red-700">Reste a payer</span>
                    </div>
                    <p className="text-2xl font-black text-red-600">
                      {(dashboardStats.resteAPayer - dashboardStats.montantPartielRestant).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Échéancier - Alertes - Design Premium */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Documents en retard */}
            <Card className={`border-0 shadow-xl overflow-hidden ${dashboardStats.enRetard > 0 ? 'ring-2 ring-red-200' : ''}`}>
              <CardHeader className={`pb-3 ${dashboardStats.enRetard > 0 ? 'bg-gradient-to-r from-red-500 to-rose-500' : 'bg-gradient-to-r from-gray-100 to-gray-50'}`}>
                <CardTitle className={`text-base flex items-center gap-3 ${dashboardStats.enRetard > 0 ? 'text-white' : 'text-gray-700'}`}>
                  <div className={`p-2 rounded-lg ${dashboardStats.enRetard > 0 ? 'bg-white/20' : 'bg-red-600'}`}>
                    <AlertTriangle className={`h-5 w-5 ${dashboardStats.enRetard > 0 ? 'text-white' : 'text-red-600'}`} />
                  </div>
                  En Retard (+30j)
                  <span className={`ml-auto px-3 py-1 rounded-full text-sm font-bold ${dashboardStats.enRetard > 0 ? 'bg-white/20 text-white' : 'bg-red-600 text-white'}`}>
                    {dashboardStats.enRetard}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {dashboardStats.enRetard > 0 ? (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {dashboardStats.documentsEnRetard.slice(0, 5).map((doc, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-red-50 hover:bg-red-100 rounded-xl border border-red-100 transition-colors group">
                        <div className="truncate flex-1 mr-3">
                          <p className="font-semibold truncate text-gray-900">{doc.raison_sociale}</p>
                          <p className="text-sm text-red-600 font-medium">{calculateMontantTTC(doc).toLocaleString('fr-FR')} €</p>
                        </div>
                        <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white shadow-sm opacity-80 group-hover:opacity-100" onClick={() => openValidationModal(doc)}>
                          Valider
                        </Button>
                      </div>
                    ))}
                    {dashboardStats.enRetard > 5 && (
                      <button onClick={() => setActiveTab('attente')} className="w-full text-sm text-center text-red-600 hover:text-red-700 font-medium py-2 hover:bg-red-50 rounded-lg transition-colors">
                        Voir +{dashboardStats.enRetard - 5} autre(s) →
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="p-3 bg-gray-100 rounded-full w-fit mx-auto mb-3">
                      <CheckCircle className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Aucun document en retard</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents urgents */}
            <Card className={`border-0 shadow-xl overflow-hidden ${dashboardStats.urgents > 0 ? 'ring-2 ring-amber-200' : ''}`}>
              <CardHeader className={`pb-3 ${dashboardStats.urgents > 0 ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-gray-100 to-gray-50'}`}>
                <CardTitle className={`text-base flex items-center gap-3 ${dashboardStats.urgents > 0 ? 'text-white' : 'text-gray-700'}`}>
                  <div className={`p-2 rounded-lg ${dashboardStats.urgents > 0 ? 'bg-white/20' : 'bg-amber-600'}`}>
                    <CalendarClock className={`h-5 w-5 ${dashboardStats.urgents > 0 ? 'text-white' : 'text-amber-600'}`} />
                  </div>
                  Urgents (15-30j)
                  <span className={`ml-auto px-3 py-1 rounded-full text-sm font-bold ${dashboardStats.urgents > 0 ? 'bg-white/20 text-white' : 'bg-amber-600 text-white'}`}>
                    {dashboardStats.urgents}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {dashboardStats.urgents > 0 ? (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {dashboardStats.documentsUrgents.slice(0, 5).map((doc, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-amber-50 hover:bg-amber-100 rounded-xl border border-amber-100 transition-colors group">
                        <div className="truncate flex-1 mr-3">
                          <p className="font-semibold truncate text-gray-900">{doc.raison_sociale}</p>
                          <p className="text-sm text-amber-600 font-medium">{calculateMontantTTC(doc).toLocaleString('fr-FR')} €</p>
                        </div>
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm opacity-80 group-hover:opacity-100" onClick={() => openValidationModal(doc)}>
                          Valider
                        </Button>
                      </div>
                    ))}
                    {dashboardStats.urgents > 5 && (
                      <button onClick={() => setActiveTab('attente')} className="w-full text-sm text-center text-amber-600 hover:text-amber-700 font-medium py-2 hover:bg-amber-50 rounded-lg transition-colors">
                        Voir +{dashboardStats.urgents - 5} autre(s) →
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="p-3 bg-gray-100 rounded-full w-fit mx-auto mb-3">
                      <CheckCircle className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Aucun document urgent</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents récents */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardHeader className="pb-3 bg-gradient-to-r from-emerald-500 to-teal-500">
                <CardTitle className="text-base flex items-center gap-3 text-white">
                  <div className="p-2 rounded-lg bg-white/20">
                    <CalendarDays className="h-5 w-5 text-white" />
                  </div>
                  Récents (-15j)
                  <span className="ml-auto px-3 py-1 rounded-full text-sm font-bold bg-white/20 text-white">
                    {dashboardStats.recents}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {dashboardStats.recents > 0 ? (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {dashboardStats.documentsRecents.slice(0, 5).map((doc, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl border border-emerald-100 transition-colors group">
                        <div className="truncate flex-1 mr-3">
                          <p className="font-semibold truncate text-gray-900">{doc.raison_sociale}</p>
                          <p className="text-sm text-emerald-600 font-medium">{calculateMontantTTC(doc).toLocaleString('fr-FR')} €</p>
                        </div>
                        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm opacity-80 group-hover:opacity-100" onClick={() => openValidationModal(doc)}>
                          Valider
                        </Button>
                      </div>
                    ))}
                    {dashboardStats.recents > 5 && (
                      <button onClick={() => setActiveTab('attente')} className="w-full text-sm text-center text-emerald-600 hover:text-emerald-700 font-medium py-2 hover:bg-emerald-50 rounded-lg transition-colors">
                        Voir +{dashboardStats.recents - 5} autre(s) →
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="p-3 bg-gray-100 rounded-full w-fit mx-auto mb-3">
                      <FileText className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Aucun document récent</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Alerte : Souscriptions sans email (bloquant SEPA) */}
          {dashboardStats.sansEmail > 0 && (
            <Card className="border-0 shadow-xl overflow-hidden ring-2 ring-orange-300" data-testid="alert-sans-email">
              <CardHeader className="pb-3 bg-gradient-to-r from-orange-600 to-red-600">
                <CardTitle className="text-base flex items-center gap-3 text-white">
                  <div className="p-2 rounded-lg bg-white/20">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  Souscriptions sans email - Bloquant SEPA
                  <span className="ml-auto px-3 py-1 rounded-full text-sm font-bold bg-white/20 text-white" data-testid="sans-email-count">
                    {dashboardStats.sansEmail}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-3">
                  Ces souscriptions en attente de paiement n'ont pas d'adresse email configurée.
                  Les prélèvements SEPA ne pourront pas être initiés sans email client.
                </p>
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {dashboardStats.documentsSansEmail.slice(0, 8).map((doc, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-orange-50 hover:bg-orange-100 rounded-xl border border-orange-200 transition-colors group">
                      <div className="truncate flex-1 mr-3">
                        <p className="font-semibold truncate text-gray-900">{doc.raison_sociale}</p>
                        <p className="text-xs text-gray-500">{doc.numero_police}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-orange-700">{calculateMontantTTC(doc).toLocaleString('fr-FR')} €</span>
                        <Badge className="bg-orange-600 text-white text-xs font-semibold">Sans email</Badge>
                      </div>
                    </div>
                  ))}
                  {dashboardStats.sansEmail > 8 && (
                    <p className="text-sm text-center text-orange-600 font-medium py-2">
                      +{dashboardStats.sansEmail - 8} autre(s) souscription(s) sans email
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Liste des paiements partiels en cours */}
          {dashboardStats.paiementsPartielsEnCours > 0 && (
            <Card className="border shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-purple-600" />
                  Paiements Partiels en Cours
                  <Badge className="ml-2 bg-purple-600 text-white font-semibold">{dashboardStats.paiementsPartielsEnCours}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardStats.documentsAvecPaiementsPartiels.slice(0, 10).map((doc, idx) => {
                    const totalTTC = calculateMontantTTC(doc);
                    const montantPaye = doc.montant_paye || doc.paiements_partiels?.reduce((s, p) => s + (p.amount || p.montant || 0), 0) || 0;
                    const reste = totalTTC - montantPaye;
                    const progression = totalTTC > 0 ? ((montantPaye / totalTTC) * 100).toFixed(0) : 0;
                    
                    return (
                      <div key={idx} className="p-4 border rounded-lg bg-white hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold">{doc.raison_sociale}</p>
                            <p className="text-sm text-gray-500">{doc.numero_police || doc.id}</p>
                          </div>
                          <Badge className="bg-purple-600 text-white font-semibold">
                            {doc.paiements_partiels?.length || 0} paiement(s)
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Progression</span>
                            <span className="font-semibold">{progression}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2.5 rounded-full"
                              style={{ width: `${progression}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-sm pt-1">
                            <div>
                              <span className="text-gray-500">Payé: </span>
                              <span className="font-medium text-green-600">{montantPaye.toLocaleString('fr-FR')} €</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Reste: </span>
                              <span className="font-medium text-red-600">{reste.toLocaleString('fr-FR')} €</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Total: </span>
                              <span className="font-medium">{totalTTC.toLocaleString('fr-FR')} €</span>
                            </div>
                          </div>
                          {/* Modes de paiement utilisés */}
                          {doc.paiements_partiels && doc.paiements_partiels.length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-2 border-t mt-2">
                              {doc.paiements_partiels.slice(0, 4).map((p, pidx) => {
                                const methodConfig = getPaymentMethodConfig(p.method || p.mode_paiement);
                                return (
                                  <span key={pidx} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${methodConfig.color}`}>
                                    {methodConfig.icon === 'Landmark' && <Landmark className="h-3 w-3" />}
                                    {methodConfig.icon === 'Repeat' && <Repeat className="h-3 w-3" />}
                                    {methodConfig.icon === 'CreditCard' && <CreditCard className="h-3 w-3" />}
                                    {methodConfig.icon === 'Banknote' && <Banknote className="h-3 w-3" />}
                                    {methodConfig.icon === 'Euro' && <Euro className="h-3 w-3" />}
                                    {(p.amount || p.montant || 0).toLocaleString('fr-FR')}€
                                  </span>
                                );
                              })}
                              {doc.paiements_partiels.length > 4 && (
                                <span className="text-xs text-gray-500">+{doc.paiements_partiels.length - 4}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-purple-600 hover:bg-purple-700"
                            onClick={() => openPartialPaymentModal(doc)}
                          >
                            <Banknote className="h-4 w-4 mr-1" />
                            Ajouter paiement
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openValidationModal(doc)}
                          >
                            Solder
                          </Button>
                          {/* Boutons échéancier */}
                          {doc.echeancier && doc.echeancier.length > 0 && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                onClick={() => printEcheancier(doc)}
                                title="Imprimer l'échéancier"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                onClick={() => handleResetEcheancier(doc)}
                                disabled={!doc.echeancier.some(e => e.paye || e.statut === 'paye')}
                                title={doc.echeancier.some(e => e.paye || e.statut === 'paye') 
                                  ? "Réinitialiser l'échéancier" 
                                  : "Aucune échéance payée à réinitialiser"}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleDeleteEcheancier(doc)}
                                title="Supprimer l'échéancier"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
  );
}
